import axios, { AxiosRequestConfig } from 'axios';
import { getCookie, setCookie } from './cookie';

// Flag to track if we're currently refreshing token
let isRefreshingToken = false;

// Helper function to refresh access token (exported for use in fetch-based requests)
export async function refreshAccessTokenForFetch(): Promise<string | null> {
  // Use the same refresh logic as the axios version
  return refreshAccessToken();
}

// Helper function to refresh access token
async function refreshAccessToken(): Promise<string | null> {
  // Prevent multiple simultaneous refresh attempts
  if (isRefreshingToken) {
    console.log('WhatsApp service: Token refresh already in progress, waiting...');
    // Wait a bit for the current refresh to complete
    await new Promise(resolve => setTimeout(resolve, 500));
    // Check if token was refreshed by another request
    const newToken = getCookie('token');
    if (newToken) {
      console.log('WhatsApp service: Token was refreshed by another request');
      return newToken;
    }
    return null;
  }

  try {
    isRefreshingToken = true;
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      console.log('WhatsApp service: No refresh token available in localStorage');
      isRefreshingToken = false;
      return null;
    }

    console.log('WhatsApp service: Attempting to refresh token...');
    const baseURL = 'https://api.frontdesk24.com';
    const response = await axios.post(`${baseURL}/api/refresh`, {
      refreshToken
    });

    console.log('WhatsApp service: Refresh token response:', response.status, response.data);

    if (response.data && typeof response.data === 'object' && 'accessToken' in response.data) {
      const newAccessToken = (response.data as any).accessToken;
      // Update the token cookie
      setCookie('token', newAccessToken, 7);
      console.log('WhatsApp service: Token refreshed successfully, new token saved');
      isRefreshingToken = false;
      return newAccessToken;
    }
    
    console.warn('WhatsApp service: Refresh response does not contain accessToken');
    isRefreshingToken = false;
    return null;
  } catch (error: any) {
    console.error('WhatsApp service: Error refreshing token:', error);
    if (error.response) {
      console.error('WhatsApp service: Refresh token API error:', error.response.status, error.response.data);
      // If refresh token itself is invalid (401/403), it means refresh token expired
      if (error.response.status === 401 || error.response.status === 403) {
        console.error('WhatsApp service: Refresh token is invalid or expired');
      }
    }
    isRefreshingToken = false;
    return null;
  }
}

// Helper function to make authenticated request with automatic token refresh
async function makeAuthenticatedRequest<T>(config: AxiosRequestConfig): Promise<T> {
  let token = getCookie('token');
  
  // If no token, try to refresh first (only if refresh token exists)
  if (!token) {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      console.log('WhatsApp service: No access token found, but refresh token exists. Attempting to refresh...');
      const newToken = await refreshAccessToken();
      if (newToken) {
        token = newToken;
        console.log('WhatsApp service: Successfully refreshed token');
      } else {
        // Refresh failed - throw error but don't redirect
        // Let the component handle the error or retry
        console.error('WhatsApp service: Failed to refresh token, refresh token may be invalid');
        throw new Error('Failed to refresh authentication token');
      }
    } else {
      // No refresh token at all - throw error but don't redirect
      console.error('WhatsApp service: No access token and no refresh token available');
      throw new Error('Authentication token is required');
    }
  }

  // Set authorization header
  config.headers = {
    ...config.headers,
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };

  try {
    const response = await axios.request<T>(config);
    return response.data;
  } catch (error: any) {
    // Handle 401, 402, 403 errors - try to refresh token
    if (error.response && (error.response.status === 401 || error.response.status === 402 || error.response.status === 403)) {
      // Avoid infinite retry loop
      if ((config as any)._retry) {
        console.error('WhatsApp service: Token refresh already attempted, refresh token may be invalid');
        // Don't redirect - throw error and let component handle it
        throw error;
      }

      (config as any)._retry = true;
      
      console.log('WhatsApp service: Token expired (status:', error.response.status, '), attempting to refresh...');
      const newToken = await refreshAccessToken();
      
      if (newToken) {
        // Update the authorization header with new token
        config.headers = {
          ...config.headers,
          'Authorization': `Bearer ${newToken}`
        };
        
        // Retry the original request with new token
        console.log('WhatsApp service: Token refreshed successfully, retrying original request...');
        try {
          const response = await axios.request<T>(config);
          console.log('WhatsApp service: Request succeeded after token refresh');
          return response.data;
        } catch (retryError: any) {
          // If retry also fails with 401/402/403, refresh token is invalid
          if (retryError.response && (retryError.response.status === 401 || retryError.response.status === 402 || retryError.response.status === 403)) {
            console.error('WhatsApp service: Request still failing after refresh, refresh token may be invalid');
            // Don't redirect - throw error and let component handle it
            throw retryError;
          }
          throw retryError;
        }
      } else {
        // Refresh token doesn't exist or is invalid
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          console.error('WhatsApp service: No refresh token available');
        } else {
          console.error('WhatsApp service: Failed to refresh token - refresh token may be invalid or expired');
        }
        // Don't redirect - throw error and let component handle it
        throw error;
      }
    }
    
    throw error;
  }
}

// Helper function to make authenticated fetch requests with automatic token refresh
export async function makeAuthenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  let token = getCookie('token');
  
  // If no token, try to refresh first (only if refresh token exists)
  if (!token) {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      console.log('Fetch service: No access token found, but refresh token exists. Attempting to refresh...');
      const newToken = await refreshAccessTokenForFetch();
      if (newToken) {
        token = newToken;
        console.log('Fetch service: Successfully refreshed token');
      } else {
        console.error('Fetch service: Failed to refresh token, refresh token may be invalid');
        throw new Error('Failed to refresh authentication token');
      }
    } else {
      console.error('Fetch service: No access token and no refresh token available');
      throw new Error('Authentication token is required');
    }
  }

  // Set authorization header
  const headers = new Headers(options.headers);
  headers.set('Authorization', `Bearer ${token}`);
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }

  const requestOptions: RequestInit = {
    ...options,
    headers,
  };

  try {
    const response = await fetch(url, requestOptions);
    
    // Handle 401, 402, 403 errors - try to refresh token
    if (response.status === 401 || response.status === 402 || response.status === 403) {
      // Avoid infinite retry loop
      if ((requestOptions as any)._retry) {
        console.error('Fetch service: Token refresh already attempted, refresh token may be invalid');
        throw new Error('Authentication failed after token refresh');
      }

      (requestOptions as any)._retry = true;
      
      console.log('Fetch service: Token expired (status:', response.status, '), attempting to refresh...');
      const newToken = await refreshAccessTokenForFetch();
      
      if (newToken) {
        // Update the authorization header with new token
        headers.set('Authorization', `Bearer ${newToken}`);
        
        // Retry the original request with new token
        console.log('Fetch service: Token refreshed successfully, retrying original request...');
        const retryOptions: RequestInit = {
          ...options,
          headers,
        };
        
        const retryResponse = await fetch(url, retryOptions);
        
        if (retryResponse.ok || (retryResponse.status !== 401 && retryResponse.status !== 402 && retryResponse.status !== 403)) {
          console.log('Fetch service: Request succeeded after token refresh');
          return retryResponse;
        } else {
          console.error('Fetch service: Request still failing after refresh, refresh token may be invalid');
          throw new Error('Authentication failed after token refresh');
        }
      } else {
        // Refresh token doesn't exist or is invalid
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          console.error('Fetch service: No refresh token available');
        } else {
          console.error('Fetch service: Failed to refresh token - refresh token may be invalid or expired');
        }
        throw new Error('Failed to refresh authentication token');
      }
    }
    
    return response;
  } catch (error: any) {
    console.error('Fetch service: Request failed:', error);
    throw error;
  }
}

export interface WhatsAppAccountResponse {
  version: string;
  event: string;
  businessId: string;
  user_email: string;
  updatedAt: string;
  createdAt: string;
  phoneNumberId: string;
  wabaId: string;
  type: string;
}

export interface WhatsAppAccount {
  id: string;
  name: string;
  phoneNumberId: string;
  wabaId: string;
  phoneNumber: string;
  businessName?: string;
  status?: 'active' | 'inactive' | 'pending';
}

export const fetchWhatsAppAccounts = async (userEmail: string): Promise<WhatsAppAccount[]> => {
  try {
    console.log('WhatsApp service: Fetching accounts for email:', userEmail);
    
    // Add timeout and retry logic
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    const response = await axios.get(`https://api.frontdesk24.com/api/whatsapp/${userEmail}`, {
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    clearTimeout(timeoutId);
    console.log('WhatsApp service: API response:', response.data);
    
    if (response.data && Array.isArray(response.data)) {
      // Transform API response to our WhatsAppAccount format
      const transformedAccounts = response.data.map((account: WhatsAppAccountResponse, index: number) => ({
        id: `phone_${index + 1}`,
        name: `WhatsApp Account ${index + 1}`,
        phoneNumberId: account.phoneNumberId,
        wabaId: account.wabaId,
        phoneNumber: `+${account.phoneNumberId}`, // Using phoneNumberId as phone number
        businessName: `Business ${account.businessId}`,
        status: 'active' as const
      }));
      
      console.log('WhatsApp service: Transformed accounts:', transformedAccounts);
      return transformedAccounts;
    }
    
    console.log('WhatsApp service: No accounts found in response');
    return [];
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error('WhatsApp service: Request timeout');
    } else if (error.response) {
      console.error('WhatsApp service: API error response:', error.response.status, error.response.data);
    } else if (error.request) {
      console.error('WhatsApp service: Network error:', error.request);
    } else {
      console.error('WhatsApp service: Error fetching WhatsApp accounts:', error.message);
    }
    return [];
  }
};

// Helper function to get account by ID
export const getAccountById = (accounts: WhatsAppAccount[], id: string): WhatsAppAccount | undefined => {
  return accounts.find(account => account.id === id);
};

// Helper function to get active accounts only
export const getActiveAccounts = (accounts: WhatsAppAccount[]): WhatsAppAccount[] => {
  return accounts.filter(account => account.status === 'active');
};

// Helper function to get default account
export const getDefaultAccount = (accounts: WhatsAppAccount[]): WhatsAppAccount | null => {
  const activeAccounts = getActiveAccounts(accounts);
  return activeAccounts.length > 0 ? activeAccounts[0] : null;
};

// Interface for Flow response from API
export interface FlowResponse {
  id: string;
  name: string;
  description?: string;
  category?: string;
  icon?: string;
  screens?: any[];
  createdAt?: string;
  updatedAt?: string;
  wabaId?: string;
}

// Fetch flows from API
export const fetchFlows = async (wabaId: string): Promise<FlowResponse[]> => {
  console.log('WhatsApp service: fetchFlows function called with wabaId:', wabaId);
  
  try {
    console.log('WhatsApp service: Fetching flows for wabaId:', wabaId);
    
    if (!wabaId) {
      console.warn('WhatsApp service: wabaId is missing');
      return [];
    }
    
    const url = `https://api.frontdesk24.com/api/flows/meta/waba/${wabaId}`;
    console.log('WhatsApp service: Making request to URL:', url);
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    // Let makeAuthenticatedRequest handle token validation and refresh
    // It will check for token and refresh if needed
    console.log('WhatsApp service: About to make authenticated request...');
    const response = await makeAuthenticatedRequest({
      url,
      method: 'GET',
      signal: controller.signal
    } as AxiosRequestConfig);
    
    clearTimeout(timeoutId);
    console.log('WhatsApp service: Flows API response:', JSON.stringify(response, null, 2));
    
    // Handle different response structures
    let flows: FlowResponse[] = [];
    
    if (response && Array.isArray(response)) {
      // Direct array response
      console.log('WhatsApp service: Found direct array response');
      flows = response as FlowResponse[];
    } else if ((response as any)?.success && (response as any)?.data) {
      // Nested structure: { success: true, data: { flows: [...], wabaId: "...", count: number } }
      console.log('WhatsApp service: Response has success and data:', (response as any).success);
      
      if ((response as any).data.flows && Array.isArray((response as any).data.flows)) {
        // Structure: { success: true, data: { flows: [...] } }
        console.log('WhatsApp service: Found flows in data.flows:', (response as any).data.flows.length);
        flows = (response as any).data.flows;
      } else if ((response as any).data.data && Array.isArray((response as any).data.data)) {
        // Structure: { success: true, data: { data: [...] } }
        console.log('WhatsApp service: Found flows in data.data.data:', (response as any).data.data.length);
        flows = (response as any).data.data;
      } else if (Array.isArray((response as any).data)) {
        // Structure: { success: true, data: [...] }
        console.log('WhatsApp service: Found flows in data.data:', (response as any).data.length);
        flows = (response as any).data;
      }
    } else if ((response as any)?.data && Array.isArray((response as any).data)) {
      // Structure: { data: [...] }
      console.log('WhatsApp service: Found flows in data.data:', (response as any).data.length);
      flows = (response as any).data;
    } else if ((response as any)?.flows && Array.isArray((response as any).flows)) {
      // Structure: { flows: [...] }
      console.log('WhatsApp service: Found flows in flows:', (response as any).flows.length);
      flows = (response as any).flows;
    }
    
    if (flows.length > 0) {
      console.log('WhatsApp service: Returning', flows.length, 'flows:', flows);
      return flows;
    }
    
    console.error('WhatsApp service: No flows found in response. Response structure:', JSON.stringify(response, null, 2));
    console.error('WhatsApp service: Checking response.success:', (response as any)?.success);
    console.error('WhatsApp service: Checking response.data:', (response as any)?.data);
    console.error('WhatsApp service: Checking response.data.data:', (response as any)?.data?.data);
    return [];
  } catch (error: any) {
    console.error('WhatsApp service: Error in fetchFlows:', error);
    
    // If error is from makeAuthenticatedRequest after failed token refresh,
    // it will redirect to signin, so we should let it propagate
    if (error.message && error.message.includes('Authentication')) {
      console.error('WhatsApp service: Authentication error propagated from makeAuthenticatedRequest');
      throw error;
    }
    
    if (error.name === 'AbortError') {
      console.error('WhatsApp service: Request timeout');
      throw new Error('Request timeout while fetching flows');
    } else if (error.response) {
      const status = error.response.status;
      console.error('WhatsApp service: API error response:', status, error.response.data);
      
      // If makeAuthenticatedRequest handled token refresh, it should have retried
      // If we're here, either token refresh failed or there's another error
      if (status === 402 || status === 401 || status === 403) {
        console.error('WhatsApp service: Authentication error after token refresh attempt');
        // makeAuthenticatedRequest should have already tried to refresh and redirect
        // If we're here, it means refresh failed - rethrow so component can handle
        throw error;
      }
      
      // For other errors, throw to let component handle
      throw error;
    } else if (error.request) {
      console.error('WhatsApp service: Network error:', error.request);
      throw new Error('Network error while fetching flows');
    } else {
      console.error('WhatsApp service: Error fetching flows:', error.message);
      throw error;
    }
  }
};

// Fetch a single flow by ID
export const fetchFlowById = async (flowId: string, wabaId: string): Promise<FlowResponse | null> => {
  try {
    console.log('WhatsApp service: Fetching flow by ID:', flowId, 'for wabaId:', wabaId);
    
    if (!flowId || !wabaId) {
      console.warn('WhatsApp service: flowId or wabaId is missing');
      return null;
    }
    
    const url = `https://api.frontdesk24.com/api/flows/${flowId}?wabaId=${wabaId}`;
    console.log('WhatsApp service: Making request to URL:', url);
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    
    // Let makeAuthenticatedRequest handle token validation and refresh
    // It will check for token and refresh if needed
    console.log('WhatsApp service: About to make authenticated request for flow...');
    const response = await makeAuthenticatedRequest<any>({
      url,
      method: 'GET',
      signal: controller.signal
    } as AxiosRequestConfig);
    
    clearTimeout(timeoutId);
    console.log('WhatsApp service: Flow API response:', JSON.stringify(response, null, 2));
    
    // Handle different response structures
    if (response) {
      if ((response as any).data) {
        return (response as any).data;
      }
      return response as FlowResponse;
    }
    
    return null;
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error('WhatsApp service: Request timeout');
    } else if (error.response) {
      console.error('WhatsApp service: API error response:', error.response.status, error.response.data);
    } else {
      console.error('WhatsApp service: Error fetching flow:', error.message);
    }
    throw error;
  }
};

// Fetch flow meta for preview
export const fetchFlowMeta = async (flowId: string, wabaId: string): Promise<any> => {
  try {
    console.log('WhatsApp service: Fetching flow meta for flowId:', flowId, 'wabaId:', wabaId);
    
    if (!flowId || !wabaId) {
      console.warn('WhatsApp service: flowId or wabaId is missing');
      return null;
    }
    
    // Call the meta endpoint for a single flow
    const url = `https://api.frontdesk24.com/api/flows/meta/${flowId}?wabaId=${wabaId}`;
    console.log('WhatsApp service: Making request to URL:', url);
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    
    const response = await makeAuthenticatedRequest<any>({
      url,
      method: 'GET',
      signal: controller.signal
    } as AxiosRequestConfig);
    
    clearTimeout(timeoutId);
    console.log('WhatsApp service: Flow meta API response:', JSON.stringify(response, null, 2));
    
    // Handle response structure
    if (response) {
      if ((response as any)?.data) {
        console.log('WhatsApp service: Found flow meta in response.data');
        return (response as any).data;
      }
      if ((response as any)?.success && (response as any)?.data) {
        console.log('WhatsApp service: Found flow meta in response.data (success structure)');
        return (response as any).data;
      }
      console.log('WhatsApp service: Returning response directly');
      return response;
    }
    
    console.warn('WhatsApp service: No flow meta found in response');
    return null;
  } catch (error: any) {
    console.error('WhatsApp service: Error fetching flow meta:', error);
    throw error;
  }
};

// Create/Publish a flow
export const createFlow = async (wabaId: string, flowData: {
  name: string;
  categories: string[];
  flowJson: object;
  publish: boolean;
  endpoint_uri: string;
}): Promise<any> => {
  try {
    console.log('WhatsApp service: Creating flow with wabaId:', wabaId);
    console.log('WhatsApp service: Flow data:', flowData);
    
    if (!wabaId) {
      throw new Error('WABA ID is required');
    }
    
    const url = `https://api.frontdesk24.com/api/flows/meta/waba/${wabaId}`;
    console.log('WhatsApp service: Making POST request to URL:', url);
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout for publish
    
    const response = await makeAuthenticatedRequest<any>({
      url,
      method: 'POST',
      data: flowData,
      signal: controller.signal
    } as AxiosRequestConfig);
    
    clearTimeout(timeoutId);
    console.log('WhatsApp service: Flow created successfully:', response);
    
    return response;
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error('WhatsApp service: Request timeout');
      throw new Error('Request timeout while creating flow');
    } else if (error.response) {
      console.error('WhatsApp service: API error response:', error.response.status, error.response.data);
      throw error;
    } else {
      console.error('WhatsApp service: Error creating flow:', error.message);
      throw error;
    }
  }
};

// Delete a flow by ID
export const deleteFlow = async (flowId: string, wabaId: string): Promise<boolean> => {
  try {
    console.log('WhatsApp service: Deleting flow ID:', flowId, 'for wabaId:', wabaId);
    
    if (!flowId || !wabaId) {
      console.warn('WhatsApp service: flowId or wabaId is missing');
      throw new Error('Flow ID and WABA ID are required');
    }
    
    const url = `https://api.frontdesk24.com/api/flows/${flowId}?wabaId=${wabaId}`;
    console.log('WhatsApp service: Making DELETE request to URL:', url);
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    
    console.log('WhatsApp service: About to make axios DELETE request for flow...');
    await makeAuthenticatedRequest<any>({
      url,
      method: 'DELETE',
      signal: controller.signal
    } as AxiosRequestConfig);
    
    clearTimeout(timeoutId);
    console.log('WhatsApp service: Flow deleted successfully');
    
    return true;
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error('WhatsApp service: Request timeout');
      throw new Error('Request timeout while deleting flow');
    } else if (error.response) {
      console.error('WhatsApp service: API error response:', error.response.status, error.response.data);
      throw error;
    } else {
      console.error('WhatsApp service: Error deleting flow:', error.message);
      throw error;
    }
  }
};
